/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import CsvExportButton from "../components/CsvExportButton";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";

/** ---------- CONFIG ---------- */
// Both files now served via the mindgamedataapi CloudFront
const METADATA_URL =
  "https://d1ct3zodbaiefe.cloudfront.net/metadata.csv.gz"; // CF
const REPORT_CARDS_URL =
  "https://d1ct3zodbaiefe.cloudfront.net/report_cards.csv.gz"; // <-- CHANGED to CF

// Display order + CSV column keys to pull from report_cards (normalized)
const CATEGORIES = [
  { label: "mindshare_absolute", key: "mindshare_absolute" },
  { label: "mindshare_growth", key: "mindshare_growth" },
  { label: "mindshare_rank", key: "mindshare_rank" },
  { label: "web_search_rank", key: "web_search_rank" },
  { label: "youtube_search_rank", key: "youtube_search_rank" },
  { label: "twitch_viewtime_rank", key: "twitch_viewtime_rank" },
  { label: "search_volume", key: "search_volume" },
  { label: "overall_grade_prediction", key: "overall_grade_prediction" },
];

// canonical week columns for both grids (-24..+4 inclusive)
const WEEKS = Array.from({ length: 29 }, (_, i) => -24 + i);

// grade color palette (mindGAME dark)
const gradeColor = (g) => {
  const x = (g || "").toString().trim().toUpperCase();
  switch (x) {
    case "S":
      return "#0ea5e9";
    case "A":
      return "#22d3ee";
    case "B":
      return "#22c55e";
    case "C":
      return "#eab308";
    case "D":
      return "#f59e0b";
    case "F":
      return "#ef4444";
    default:
      return "#2b2f36"; // neutral gray for "no data"
  }
};

// With correct Content-Encoding: gzip from CloudFront, the browser auto-decompresses.
// So just read text; no DecompressionStream/pako needed.
// Robust gzip-aware CSV fetcher (handles both auto-decompressed and raw .gz bodies)
async function fetchGzipCsv(url) {
  const res = await fetch(url, { mode: "cors", cache: "default" });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText} for ${url}`);

  const encoding = res.headers.get("content-encoding") || "";
  let text;

  // 1) If the browser/CF already decompressed (Content-Encoding present),
  //    just read as text.
  if (/gzip|br|deflate/i.test(encoding)) {
    text = (await res.text()).trim();
  } else if (url.endsWith(".gz") && "DecompressionStream" in window && res.body) {
    // 2) Raw .gz with no Content-Encoding: use DecompressionStream if available
    try {
      const ds = new DecompressionStream("gzip");
      const reader = res.body.pipeThrough(ds).getReader();
      const chunks = [];
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
      }
      text = (
        await new Blob(chunks, { type: "text/plain;charset=utf-8" }).text()
      ).trim();
    } catch {
      // fall through to ArrayBuffer path below
    }
  }

  // 3) Fallback: manually gunzip/UTF-8 decode
  if (!text) {
    if (url.endsWith(".gz")) {
      const buf = await res.arrayBuffer();
      const u8 = new Uint8Array(buf);
      const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
      if (looksGzip) {
        try {
          const { default: pako } = await import("pako");
          text = pako.ungzip(u8, { to: "string" }).trim();
        } catch {
          text = new TextDecoder("utf-8").decode(u8).trim();
        }
      } else {
        text = new TextDecoder("utf-8").decode(u8).trim();
      }
    } else {
      text = (await res.text()).trim();
    }
  }

  const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
  return normalizeHeaders(parsed);
}


// normalize headers to your snake_case keys
function normalizeHeaders(parsed) {
  const norm = (s) =>
    s
      .toLowerCase()
      .replace(/\s+|\//g, "_")
      .replace(/[()]+/g, "")
      .replace(/_{2,}/g, "_")
      .trim();
  const map = {};
  parsed.meta.fields.forEach((f) => (map[f] = norm(f)));
  return parsed.data.map((r) => {
    const n = {};
    for (const k in r) n[map[k]] = (r[k] ?? "").toString().trim();
    return n;
  });
}

/** helper: safely pick the first available key from an object */
const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

/** helper: canonical game name field from any row */
const gameNameFromRow = (row) =>
  pick(row, ["game", "name", "title", "gamename", "game_name"]);

export default function Scorecards() {
  const [meta, setMeta] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [gameOpt, setGameOpt] = useState(null);

  // genre-average launch-month grade filter (week 0)
  const [genreLaunchGrade, setGenreLaunchGrade] = useState("C");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [m, r] = await Promise.all([
          fetchGzipCsv(METADATA_URL),
          fetchGzipCsv(REPORT_CARDS_URL),
        ]);
        setMeta(m);
        setCards(r);

        // Default to "Elden Ring" if present
        const names = new Set(
  m.map((row) => gameNameFromRow(row).trim().toLowerCase())
);

        if (names.has("elden ring")) {
          setGameOpt({ label: "Elden Ring", value: "Elden Ring" });
        }
      } catch (e) {
        console.error("Failed to load scorecard inputs:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Joinable game list: only keep games present in both datasets
  const gamesList = useMemo(() => {
  if (!meta.length || !cards.length) return [];
  const metaNames = new Set(
    meta.map((row) => gameNameFromRow(row).trim())
  );
  const rcNames = new Set(
    cards.map((row) => gameNameFromRow(row).trim())
  );
  const intersection = [...rcNames].filter((g) => metaNames.has(g));
  return intersection.sort((a, b) => a.localeCompare(b));
}, [meta, cards]);

  // react-select game options
  const gameOptions = useMemo(
    () => gamesList.map((g) => ({ label: g, value: g })),
    [gamesList]
  );

  // selected game's genre (first matching row in report_cards)
  const selectedGameGenre = useMemo(() => {
  if (!gameOpt) return null;
  return (
    cards.find((r) => gameNameFromRow(r) === gameOpt.value)?.genre || null
  );
}, [gameOpt, cards]);

  // selected game rows from report_cards -> week mapped
  const rowsForGameMap = useMemo(() => {
  const map = new Map(); // week -> row
  if (!gameOpt || !cards.length) return map;
  for (const r of cards) {
    if (gameNameFromRow(r) !== gameOpt.value) continue;
    const w = Number(r.week);
    if (!Number.isFinite(w)) continue;
    map.set(w, r);
  }
  return map;
}, [gameOpt, cards]);

  // selected game metadata (from metadata.csv) with flexible keys
  const selectedMeta = useMemo(() => {
    if (!meta.length) return null;
    const wantedName = gameOpt?.value || "Elden Ring"; // for initial render
    const row =
  meta.find(
    (m) =>
      gameNameFromRow(m).trim().toLowerCase() ===
      wantedName.trim().toLowerCase()
  ) || null;


    return {
      name: pick(row, ["name", "game", "title"]),
      imageUrl: pick(row, ["imageurl", "image_url", "image", "box_art"]),
      description: pick(row, ["description", "short_description", "summary"]),
      releaseDate: pick(row, [
        "release_date",
        "releasedate",
        "release",
        "release_date_utc",
      ]),
      publisher: pick(row, ["publisher", "publishers"]),
      developer: pick(row, ["developer", "developers"]),
      genre: pick(row, ["genre", "genres", "category"]),
    };
  }, [meta, gameOpt]);

  /** ---------- PRECOMPUTED GENRE "AVERAGE GAME" ---------- */
  const avgGameName = useMemo(() => {
    if (!selectedGameGenre || !genreLaunchGrade) return null;
    // Exact naming from backend: Average [GENRE] [GRADE]-Ranked Game
    return `Average ${selectedGameGenre} ${genreLaunchGrade}-Ranked Game`;
  }, [selectedGameGenre, genreLaunchGrade]);

  const rowsForAvgMap = useMemo(() => {
    const map = new Map(); // week -> row
    if (!avgGameName || !cards.length) return map;
    for (const r of cards) {
  if (gameNameFromRow(r) !== avgGameName) continue;
  const w = Number(r.week);
  if (!Number.isFinite(w)) continue;
  map.set(w, r);
}
    return map;
  }, [avgGameName, cards]);

  // Sample size: strictly from the averaged "game" at week 0 (per your engineer)
  const avgSampleSize = useMemo(() => {
    const row0 = rowsForAvgMap.get(0);
    if (!row0) return 0;
    const v =
      row0.sample_size ?? row0.samplesize ?? row0.n ?? row0.count ?? null;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }, [rowsForAvgMap]);

  // header cell style (lighter for weeks >= 0)
  const weekHeaderStyle = (w) => ({
    padding: "10px 0",
    borderLeft: "1px solid #222",
    textAlign: "center",
    color: "#cbd5e1",
    fontWeight: "700",
    fontSize: 12,
    background: w >= 0 ? "#171a1f" : "transparent",
  });

  /** ---------- CSV EXPORT (both grids + metadata) ---------- */
  const exportCSV = () => {
    const rows = [];

    // Title + timestamp
    rows.push(["Scorecards Export", new Date().toISOString()]);
    rows.push([]);

    // Metadata block
    rows.push(["Game", selectedMeta?.name || gameOpt?.value || "—"]);
    rows.push(["Genre", selectedMeta?.genre || selectedGameGenre || "—"]);
    rows.push(["Publisher", selectedMeta?.publisher || "—"]);
    rows.push(["Developer", selectedMeta?.developer || "—"]);
    rows.push([
      "Release Date",
      selectedMeta?.releaseDate
        ? String(selectedMeta.releaseDate).split("T")[0]
        : "—",
    ]);
    rows.push([]);

    // Per-game grid
    rows.push([selectedMeta?.name || gameOpt?.value || "—"]);
    rows.push(["Category", ...WEEKS]);
    CATEGORIES.forEach((cat) => {
      const line = [cat.label.replaceAll("_", " ")];
      WEEKS.forEach((w) => {
        const r = rowsForGameMap.get(w);
        const G = r && r[cat.key] ? r[cat.key].toUpperCase() : ""; // "" = no data
        line.push(G || "–");
      });
      rows.push(line);
    });

    rows.push([]);

    // Genre average grid (precomputed "Average GENRE G-Ranked Game")
    rows.push([avgGameName || "—", `Sample size: ${avgSampleSize}`]);
    rows.push(["Category", ...WEEKS]);
    CATEGORIES.forEach((cat) => {
      const line = [cat.label.replaceAll("_", " ")];
      WEEKS.forEach((w) => {
        const r = rowsForAvgMap.get(w);
        const L = r && r[cat.key] ? r[cat.key].toUpperCase() : "";
        line.push(L || "–");
      });
      rows.push(line);
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(csvBlob(blob));
    const a = document.createElement("a");
    const base = (selectedMeta?.name || gameOpt?.value || "export")
      .replace(/[^a-z0-9]+/gi, "_")
      .toLowerCase();
    a.href = url;
    a.download = `scorecards_${base}_${genreLaunchGrade}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ background: "#000", color: "#fff", minHeight: "calc(var(--mg-vh) * 100)" }}>
      {/* Toolbar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          padding: "16px 20px",
          background: "#0b0b0b",
          borderBottom: "1px solid #222",
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ fontSize: 22, margin: 0, marginRight: 12 }}>Scorecards</h1>

        <div style={{ width: 420 }}>
          <VirtualizedDropdown
            placeholder="Select a game…"
            value={gameOpt}
            onChange={setGameOpt}
            options={gamesList.map((g) => ({ label: g, value: g }))}
            isClearable
          />
        </div>

        <CsvExportButton
          onClick={exportCSV}
          disabled={loading}
          style={{
            background: "#0ea5e9",
            color: "#fff",
            border: "1px solid #0284c7",
            padding: "8px 12px",
            borderRadius: 8,
            fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          title="Export both grids and metadata to CSV"
        >
          ⬇ Export CSV
        </CsvExportButton>
      </div>

      {/* Metadata header */}
      <div style={{ padding: "16px 20px 6px" }}>
        <div
          style={{
            display: "flex",
            gap: 18,
            alignItems: "flex-start",
            border: "1px solid #1f1f1f",
            borderRadius: 12,
            padding: 16,
            background: "#0d0d0d",
          }}
        >
          <div style={{ width: 170, maxWidth: "100%" }}>
            {selectedMeta?.imageUrl ? (
              <img
                src={selectedMeta.imageUrl}
                alt={selectedMeta.name || gameOpt?.value || "box art"}
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 10,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
                }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  aspectRatio: "16/9",
                  borderRadius: 10,
                  background: "#111",
                  border: "1px solid #222",
                }}
              />
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 24, lineHeight: 1.2 }}>
              {selectedMeta?.name || gameOpt?.value || "—"}
            </h2>

            <p style={{ color: "#c9c9c9", margin: "0 0 10px", lineHeight: 1.45 }}>
              {selectedMeta?.description || "—"}
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "160px 1fr",
                gap: "6px 14px",
                fontSize: 14,
              }}
            >
              <div style={{ color: "#9aa3b2" }}>Release Date</div>
              <div>
                {selectedMeta?.releaseDate
                  ? String(selectedMeta.releaseDate).split("T")[0]
                  : "—"}
              </div>

              <div style={{ color: "#9aa3b2" }}>Publisher</div>
              <div>{selectedMeta?.publisher || "—"}</div>

              <div style={{ color: "#9aa3b2" }}>Developer</div>
              <div>{selectedMeta?.developer || "—"}</div>

              <div style={{ color: "#9aa3b2" }}>Genre</div>
              <div>{selectedMeta?.genre || selectedGameGenre || "—"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "14px 20px 40px" }}>
        {loading && <div style={{ color: "#aaa" }}>Loading…</div>}

        {/* ========= PER-GAME GRID ========= */}
        <div
          style={{
            border: "1px solid #222",
            borderRadius: 10,
            overflow: "hidden",
            background: "#0d0d0d",
            marginTop: 14,
          }}
        >
          {/* Header row: weeks */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `220px repeat(${WEEKS.length}, 52px)`,
              borderBottom: "1px solid #222",
              background: "#111",
            }}
          >
            <div
              style={{
                padding: "10px 12px",
                fontWeight: "700",
                color: "#9ca3af",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Predictions
            </div>
            {WEEKS.map((w) => (
              <div key={w} style={weekHeaderStyle(w)} title={`Week ${w}`}>
                {w}
              </div>
            ))}
          </div>

          {/* Rows for each category */}
          {CATEGORIES.map((cat, idx) => (
            <div
              key={cat.key}
              style={{
                display: "grid",
                gridTemplateColumns: `220px repeat(${WEEKS.length}, 52px)`,
                borderBottom:
                  idx === CATEGORIES.length - 1 ? "none" : "1px solid #171717",
                background: idx % 2 ? "#0b0b0b" : "#0f0f0f",
              }}
            >
              {/* Row label */}
              <div
                style={{
                  padding: "8px 12px",
                  fontWeight: 600,
                  color: "#e5e7eb",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  borderRight: "1px solid #171717",
                }}
              >
                {`${cat.label.replaceAll("_", " ")}`}
              </div>

              {/* Grades for each week */}
              {WEEKS.map((w) => {
                const row = rowsForGameMap.get(w);
                const grade = row && row[cat.key] ? row[cat.key].toUpperCase() : "";
                const isEmpty = !grade;
                return (
                  <div
                    key={w}
                    title={`Week ${w} – ${cat.label.replaceAll("_", " ")}: ${grade || "—"}`}
                    style={{
                      borderLeft: "1px solid #171717",
                      borderRight: "1px solid #0c0c0c",
                      borderTop: "1px solid #0c0c0c",
                      borderBottom: "1px solid #0c0c0c",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 34,
                      fontWeight: 800,
                      fontSize: 13,
                      color: isEmpty ? "#8b93a1" : "#0b0b0b",
                      background: gradeColor(grade),
                    }}
                  >
                    {grade || "–"}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* ========= GENRE AVERAGE GRID (Precomputed Game) ========= */}
        <div style={{ marginTop: 28 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginBottom: 10,
            }}
          >
            <h2 style={{ margin: 0 }}>
              Genre averages — {selectedGameGenre || "—"}
            </h2>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: "#ccc", fontSize: 14 }}>
                Launch-month grade filter:
              </span>
              <select
                value={genreLaunchGrade}
                onChange={(e) => setGenreLaunchGrade(e.target.value)}
                style={{
                  background: "#111",
                  color: "#fff",
                  border: "1px solid #444",
                  padding: "6px 8px",
                  borderRadius: 6,
                  fontWeight: 600,
                }}
              >
                {["S", "A", "B", "C", "D", "F"].map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>

              <span style={{ color: "#9ca3af", fontSize: 14 }}>
                Sample size:&nbsp;
                <b style={{ color: "#e5e7eb" }}>{avgSampleSize}</b>
              </span>
            </div>
          </div>

          <div
            style={{
              border: "1px solid #222",
              borderRadius: 10,
              overflow: "hidden",
              background: "#0d0d0d",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `220px repeat(${WEEKS.length}, 52px)`,
                borderBottom: "1px solid #222",
                background: "#111",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  fontWeight: 700,
                  color: "#9ca3af",
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Predictions
              </div>
              {WEEKS.map((w) => (
                <div
                  key={`gw-${w}`}
                  style={weekHeaderStyle(w)}
                  title={`Week ${w}`}
                >
                  {w}
                </div>
              ))}
            </div>

            {CATEGORIES.map((cat, idx) => (
              <div
                key={cat.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: `220px repeat(${WEEKS.length}, 52px)`,
                  borderBottom:
                    idx === CATEGORIES.length - 1 ? "none" : "1px solid #171717",
                  background: idx % 2 ? "#0b0b0b" : "#0f0f0f",
                }}
              >
                <div
                  style={{
                    padding: "8px 12px",
                    fontWeight: 700,
                    color: "#e5e7eb",
                    borderRight: "1px solid #171717",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {cat.label.replaceAll("_", " ")}
                </div>

                {WEEKS.map((w) => {
                  const r = rowsForAvgMap.get(w);
                  const L = r && r[cat.key] ? r[cat.key].toUpperCase() : "";
                  const isEmpty = !L;
                  return (
                    <div
                      key={`gcell-${cat.key}-${w}`}
                      title={`Week ${w} – ${cat.label.replaceAll("_", " ")}: ${
                        L || "—"
                      }`}
                      style={{
                        borderLeft: "1px solid #171717",
                        borderRight: "1px solid #0c0c0c",
                        borderTop: "1px solid #0c0c0c",
                        borderBottom: "1px solid #0c0c0c",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 34,
                        fontWeight: 800,
                        fontSize: 13,
                        color: isEmpty ? "#8b93a1" : "#0b0b0b",
                        background: gradeColor(L),
                      }}
                    >
                      {L || "–"}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// tiny helper so older browsers don’t choke on Blob#text presence
function csvBlob(b) { return b; }
